import { useEffect, useId, useRef } from 'react';
import { isTopDialog, popDialog, pushDialog } from './dialog-stack';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Accessibilité commune Modal/Drawer : focus initial dans le dialogue,
 * piège du Tab à l'intérieur, restauration du focus sur l'élément
 * déclencheur à la fermeture, et inscription dans la pile des dialogues.
 *
 * Renvoie l'identifiant du dialogue : `isTopDialog(id)` dit s'il est celui
 * du dessus, le seul à devoir réagir à Échap.
 */
export function useDialogA11y(open: boolean, containerRef: React.RefObject<HTMLElement | null>): string {
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const dialogId = useId();

  useEffect(() => {
    if (!open) return;
    pushDialog(dialogId);
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const container = containerRef.current;
    if (!container) return () => popDialog(dialogId);

    // Le focus initial va au premier champ du corps, pas à la croix
    // « Fermer » de l'en-tête (premier focusable du DOM) : on ouvre une
    // modale pour y saisir quelque chose, pas pour la refermer. Un `autoFocus`
    // posé par l'appelant a déjà agi quand cet effet s'exécute : on le respecte.
    if (!container.contains(document.activeElement)) {
      const focusables = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      const explicit = container.querySelector<HTMLElement>('[data-autofocus]');
      const body = focusables.filter((el) => !el.closest('[data-dialog-header]'));
      (explicit ?? body[0] ?? focusables[0] ?? container).focus();
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab' || !container || !isTopDialog(dialogId)) return;
      const items = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || active === container)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      popDialog(dialogId);
      previouslyFocused.current?.focus?.();
    };
  }, [open, containerRef, dialogId]);

  return dialogId;
}
